import { createFileRoute } from "@tanstack/react-router";
import { getRouterInstance } from "@tanstack/react-start";
import {
  isSitemapRouteIncluded,
  sitemapPathForLocation,
  sitemapXML,
  type SitemapEntry,
} from "@/lib/sitemap";
import { LOCALES, SITE_URL } from "@/lib/i18n";

// Must match the canonical/hreflang host emitted by page heads (SITE_URL in src/lib/i18n.ts).
const BASE_URL = SITE_URL;

const NON_DEFAULT_LOCALES = LOCALES.filter((locale) => locale !== "en");

// Public CMS content (kind + detail route). "case" stays out: case studies are hidden.
const CONTENT_ROUTES = [
  { kind: "blog", to: "/{-$locale}/blog/$slug" },
  { kind: "news", to: "/{-$locale}/news/$slug" },
  { kind: "industry", to: "/{-$locale}/industries/$slug" },
  { kind: "integration", to: "/{-$locale}/integrations/$slug" },
] as const;

export const Route = createFileRoute("/sitemap.xml")({
  staticData: { sitemap: false },
  server: {
    handlers: {
      GET: async () => {
        try {
          const router = await getRouterInstance();
          const entries: SitemapEntry[] = [];

          const push = (
            to: string,
            params: Record<string, string> | undefined,
            routeId: string,
            lastmod?: string,
          ) => {
            // Static pages must build without params, or the optional locale
            // segment ({-$locale}) cannot resolve and the URL gets dropped.
            const location = router.buildLocation(
              params
                ? { to, params, search: () => ({}), hash: "" }
                : { to, search: () => ({}), hash: "" },
            );
            const path = sitemapPathForLocation(router, location, routeId);
            if (path) entries.push({ path, ...(lastmod ? { lastmod } : {}) });
          };

          // Static pages, one URL per locale (no params = default English paths).
          // fullPath contains the optional `{-$locale}` marker on every route, so
          // only treat OTHER $ segments (e.g. $slug) as parameterized and skippable.
          for (const route of Object.values(router.routesById)) {
            if (!isSitemapRouteIncluded(route)) continue;
            if (/[$*]/.test(route.fullPath.replace(/\{-\$locale\}/g, ""))) continue;
            for (const locale of ["", ...NON_DEFAULT_LOCALES]) {
              push(route.fullPath, locale ? { locale } : undefined, route.id);
            }
          }

          // Route ids for parameterized pages included in the sitemap.
          const dynamicRouteIds = new Map<string, string>();
          for (const route of Object.values(router.routesById)) {
            if (isSitemapRouteIncluded(route) && /[$]/.test(route.fullPath)) {
              dynamicRouteIds.set(route.fullPath, route.id);
            }
          }

          // Published CMS content + published add-ons, paginated, session-free
          // publishable client so the sitemap is independent of visitor sessions.
          const { createClient } = await import("@supabase/supabase-js");
          const key = process.env["SUPABASE_PUBLISHABLE_KEY"]!;
          const supabase = createClient(process.env["SUPABASE_URL"]!, key, {
            auth: { persistSession: false, autoRefreshToken: false },
            global: {
              fetch: (input, init) => {
                const headers = new Headers(init?.headers);
                // Opaque sb_ keys are not JWTs; send apikey without the default bearer.
                if (key.startsWith("sb_") && headers.get("Authorization") === "Bearer " + key) {
                  headers.delete("Authorization");
                }
                headers.set("apikey", key);
                return fetch(input, { ...init, headers });
              },
            },
          });

          const pageSize = 1000;
          for (const { kind, to } of CONTENT_ROUTES) {
            const routeId = dynamicRouteIds.get(to);
            if (!routeId) continue;
            for (const locale of LOCALES) {
              for (let offset = 0; ;) {
                const { data, error } = await supabase
                  .from("content_items")
                  .select(
                    "slug, updated_at, content_translations!inner(locale, slug, title, excerpt, body, seo_title, seo_description, translation_status, updated_at)",
                  )
                  .eq("kind", kind)
                  .eq("published", true)
                  .eq("content_translations.locale", locale)
                  .order("id")
                  .range(offset, offset + pageSize - 1);
                if (error) throw new Error(`content_items/${kind}/${locale}: ${error.message}`);
                if (!data || data.length === 0) break;
                for (const row of data) {
                  const translation = row.content_translations?.[0];
                  const complete =
                    translation &&
                    translation.translation_status !== "missing" &&
                    [
                      translation.title,
                      translation.excerpt,
                      translation.body,
                      translation.seo_title,
                      translation.seo_description,
                    ].every((value) => Boolean(value?.trim()));
                  if (complete)
                    push(
                      to,
                      locale === "en"
                        ? { slug: translation.slug?.trim() || row.slug }
                        : { locale, slug: translation.slug?.trim() || row.slug },
                      routeId,
                       new Date(
                         Math.max(
                           new Date(row.updated_at).getTime(),
                           new Date(translation.updated_at).getTime(),
                         ),
                       ).toISOString(),
                    );
                }
                offset += data.length;
              }
            }
          }

          // Published add-ons live in the pricing tables, not content_items.
          const addonRouteId = dynamicRouteIds.get("/{-$locale}/addons/$slug");
          if (addonRouteId) {
            for (let offset = 0; ;) {
              const { data, error } = await supabase
                .from("pricing_addons")
                .select("slug, updated_at")
                .eq("published", true)
                .order("id")
                .range(offset, offset + pageSize - 1);
              if (error) throw new Error(`pricing_addons: ${error.message}`);
              if (!data || data.length === 0) break;
              for (const row of data) {
                for (const locale of ["", ...NON_DEFAULT_LOCALES]) {
                  push(
                    "/{-$locale}/addons/$slug",
                    locale ? { locale, slug: row.slug } : { slug: row.slug },
                    addonRouteId,
                     row.updated_at,
                  );
                }
              }
              offset += data.length;
            }
          }

          if (entries.length === 0) {
            return new Response(
              'No pages are included in this sitemap. Check route decisions and ancestor exclusions. Setting "exclude-subtree" on the root excludes the entire site.',
              { status: 404, headers: { "Cache-Control": "no-store" } },
            );
          }
          return new Response(sitemapXML(BASE_URL, entries), {
            headers: { "Content-Type": "application/xml", "Cache-Control": "public, max-age=3600" },
          });
        } catch (error) {
          console.error("sitemap generation failed", error);
          return new Response("Sitemap generation failed", {
            status: 500,
            headers: { "Cache-Control": "no-store" },
          });
        }
      },
    },
  },
});
